import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import TextItem from "../components/Text";
import { getBaseStyleClasses } from '../../core/utils/baseStyleUtils';
import { buildTargetPageUrl } from '../../core/utils/urlUtils';
import EditableElement from "../../editor/components/EditableElement";
import { EditContext } from "../../editor/context/EditContext";
import { usePreviewOptional } from "../../editor/context/PreviewContext";

const BASE_LINK_CLASSES = "inline-block hover:text-blue-500";
const CONTAINER_CLASSES = "flex space-x-4";
const TEXT_ITEM_PROPS = { returnDiv: false, ignoreSpacing: false };
const DEFAULT_BASE_STYLE = {};
const LINK_CHOICES = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

const getSectionBaseStyle = (section) =>
  section?.base_style || section?.baseStyle || DEFAULT_BASE_STYLE;

const getMergedStyles = (section) => {
  const { className: baseClass, style: baseStyle } = getBaseStyleClasses(
    getSectionBaseStyle(section),
  );
  return { baseClass, baseStyle };
};

const NavSideBarSection = ({ section, navbar, domain, username, stacked = false }) => {
  const editContext = useContext(EditContext);
  const preview = usePreviewOptional();

  // Keep UI reactive while editing this section
  const currentSection = useMemo(() => {
    const sel = editContext?.selectedElement;
    if (!sel || sel.type !== "navbar_section" || sel.id !== section.id) return section;

    const deepMerge = (target, source) => {
      const result = { ...target };
      for (const key in source) {
        const sv = source[key];
        if (sv && typeof sv === "object" && !Array.isArray(sv)) {
          result[key] = deepMerge(target[key] || {}, sv);
        } else {
          result[key] = sv;
        }
      }
      return result;
    };
    return deepMerge(section, sel);
  }, [editContext?.selectedElement, section]);

  const { baseClass, baseStyle } = getMergedStyles(currentSection);
  
  // Use the same URL building logic as Button component
  const targetPageUrl = buildTargetPageUrl(currentSection);
  const isExternal = currentSection.link_choice?.toLowerCase() === LINK_CHOICES.EXTERNAL;
  const handlePreviewNavigate = preview?.onNavigate;

  const sectionContent = (
    <ul className={stacked ? "flex flex-col space-y-2" : CONTAINER_CLASSES}>
      <li key={currentSection.id}>
        <Link
          to={targetPageUrl}
          className={`${BASE_LINK_CLASSES} ${baseClass}`}
          style={{ ...baseStyle }}
          onClick={(e) => {
            if (handlePreviewNavigate && !isExternal) {
              e.preventDefault();
              handlePreviewNavigate(targetPageUrl);
              return;
            }
            if (editContext) {
              e.preventDefault();
            }
          }}
          {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
        >
          <TextItem item={currentSection.label} {...TEXT_ITEM_PROPS} />
        </Link>
      </li>
    </ul>
  );

  return (
    <EditableElement element={{...currentSection, type: "navbar_section", website: navbar?.website, navbar: navbar}}>
      {sectionContent}
    </EditableElement>
  );
};

export default NavSideBarSection;
